/**
 * /settings/pauses (spec 015) — status da equipe AGORA e motivos de pausa.
 * Todo o staff vê; o admin configura os motivos e encerra o status de quem
 * esqueceu aberto (US1 cenário 6): quem fechou o navegador em pausa continua
 * em pausa de verdade até alguém encerrar, e o admin informa o horário REAL
 * em que a pessoa parou — senão a noite inteira vira pausa no relatório.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Presence } from '@erp/shared';
import { useAuthUser } from '@/auth/use-auth';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Dialog, EmptyState, Skeleton } from '@/components/ui/misc';
import { SettingsBreadcrumb } from '@/features/settings/settings-ui';
import { teamListQuery } from '@/features/team/team-queries';
import { cn, formatDateTime } from '@/lib/utils';
import { PauseReasonsSection } from './pause-reasons-section';
import { presenceLabel, STATUS_DOT, useNow } from './presence-chip';
import { useEndPresence } from './presence-mutations';
import { elapsedLabel, presenceErrorMessage, teamPresenceQuery } from './presence-queries';

export function PausesPage() {
  const user = useAuthUser();
  const isAdmin = user?.role === 'admin';
  return (
    <div className="space-y-4 rounded-lg border bg-card p-4 shadow-xs sm:p-5">
      <SettingsBreadcrumb page="Pausas" />
      <h1 className="text-xl leading-7 font-semibold">Pausas</h1>
      <TeamStatusSection isAdmin={isAdmin} />
      <PauseReasonsSection canEdit={isAdmin} />
    </div>
  );
}

function TeamStatusSection({ isAdmin }: { isAdmin: boolean }) {
  const teamQ = useQuery(teamPresenceQuery());
  // o status é da equipe deste tenant: os nomes saem dela (inativos inclusive)
  const usersQ = useQuery(teamListQuery({ includeInactive: true }));
  const [ending, setEnding] = useState<Presence | null>(null);
  const now = useNow(true, 30_000);

  const names = new Map((usersQ.data?.items ?? []).map((u) => [u.id, u.name]));
  // pausa primeiro (é o que se procura aqui), depois disponível, por nome
  const order = { paused: 0, available: 1, away: 2 } as const;
  const rows = [...(teamQ.data ?? [])].sort(
    (a, b) =>
      order[a.status] - order[b.status] ||
      (names.get(a.userId) ?? '').localeCompare(names.get(b.userId) ?? '')
  );

  return (
    <section className="rounded-lg border shadow-xs">
      <div className="border-b px-4 py-3">
        <h2 className="text-sm font-semibold">Equipe agora</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Quem não aparece está Ausente. Atualiza sozinho quando alguém muda o status.
        </p>
      </div>
      {teamQ.isPending ? (
        <div className="p-4">
          <Skeleton className="h-8 w-full" />
        </div>
      ) : teamQ.isError ? (
        <p className="p-4 text-sm text-destructive">{presenceErrorMessage(teamQ.error)}</p>
      ) : rows.length === 0 ? (
        <EmptyState message="Ninguém com status aberto agora" />
      ) : (
        <ul className="divide-y">
          {rows.map((p) => (
            <li key={p.userId} className="flex items-center gap-3 px-4 py-2 text-sm">
              <span className={cn('size-2 shrink-0 rounded-full', STATUS_DOT[p.status])} />
              <span className="min-w-0 flex-1 truncate font-medium">
                {names.get(p.userId) ?? `Usuário ${p.userId}`}
              </span>
              <span className="text-muted-foreground">{presenceLabel(p)}</span>
              {p.since ? (
                <span className="w-28 text-right text-xs tabular-nums text-muted-foreground" title={formatDateTime(p.since)}>
                  há {elapsedLabel(p.since, now)}
                </span>
              ) : null}
              {isAdmin ? (
                <Button variant="outline" size="sm" className="h-7" onClick={() => setEnding(p)}>
                  Encerrar
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {ending ? (
        <EndPresenceDialog
          presence={ending}
          name={names.get(ending.userId) ?? `Usuário ${ending.userId}`}
          onClose={() => setEnding(null)}
        />
      ) : null}
    </section>
  );
}

/** "aaaa-mm-ddThh:mm" local, o formato do input datetime-local */
function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function EndPresenceDialog({
  presence,
  name,
  onClose,
}: {
  presence: Presence;
  name: string;
  onClose: () => void;
}) {
  const end = useEndPresence();
  const [value, setValue] = useState(() => toLocalInput(new Date()));
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    setError(null);
    const endedAt = new Date(value);
    if (Number.isNaN(endedAt.getTime())) {
      setError('Informe o horário.');
      return;
    }
    end.mutate(
      { userId: presence.userId, endedAt: endedAt.toISOString() },
      { onSuccess: onClose, onError: (e) => setError(presenceErrorMessage(e)) }
    );
  };

  return (
    <Dialog open onClose={onClose} title={`Encerrar o status de ${name}`}>
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <p className="text-sm text-muted-foreground">
          {presenceLabel(presence)} desde{' '}
          <span className="font-medium text-foreground">{presence.since ? formatDateTime(presence.since) : '—'}</span>.
          Informe o horário em que a pessoa realmente parou — é ele que conta no relatório. Depois
          disso ela fica Ausente.
        </p>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="end-presence-at" className="text-[13px] font-semibold">
            Encerrar em
          </Label>
          <Input
            id="end-presence-at"
            type="datetime-local"
            className="h-8 w-56"
            value={value}
            min={presence.since ? toLocalInput(new Date(presence.since)) : undefined}
            max={toLocalInput(new Date())}
            onChange={(e) => setValue(e.target.value)}
          />
        </div>
        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Voltar
          </Button>
          <Button type="submit" size="sm" disabled={end.isPending}>
            Encerrar
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
